import React, { useState, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { PostProjectTaskWorkstream } from '../../redux/taskSlice';
import { Add16Regular, Delete16Regular } from '@fluentui/react-icons';
import { Menu, MenuTrigger, MenuPopover, MenuList } from '@fluentui/react-components';
import styles from './css/index.module.css';

const WorkstreamManagementMenu = ({ onWorkstreamSelect, children, taskData, selectedWorkstreamName }) => {
    const dispatch = useDispatch();
    const { projectWorkstreams } = useSelector(state => state.task);
    const { workstreams } = useSelector(state => state.dataroom);
    const [newWorkstreamName, setNewWorkstreamName] = useState('');
    const [isAdding, setIsAdding] = useState(false);
    const [open, setOpen] = useState(false);
    const selectedItemRef = useRef(null);

    // When menu opens, scroll the selected workstream into view
    useEffect(() => {
        if (!open || !selectedWorkstreamName) return;
        const timer = setTimeout(() => {
            selectedItemRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' });
        }, 0);
        return () => clearTimeout(timer);
    }, [open, selectedWorkstreamName]);

    // Get all workstreams from different sources (no duplicates)
    const getAllWorkstreams = () => {
        const workstreamMap = new Map();

        // 1. Project workstreams
        if (projectWorkstreams && projectWorkstreams.length > 0) {
            projectWorkstreams.forEach(ws => {
                workstreamMap.set(ws.name, { ...ws, source: 'project' });
            });
        }

        // 2. Dataroom workstreams
        if (workstreams && workstreams.length > 0) {
            workstreams.forEach(ws => {
                workstreamMap.set(ws.name, { ...ws, source: 'dataroom' });
            });
        }

        // 3. All workstreams from task data
        const getAllWorkstreamsFromTaskData = () => {
            // Handle both array and object structures
            let tasks = [];
            if (Array.isArray(taskData)) {
                tasks = taskData;
            } else if (taskData && taskData.data && Array.isArray(taskData.data)) {
                tasks = taskData.data;
            } else if (taskData && taskData.groups && Array.isArray(taskData.groups)) {
                // Handle grouped data - flatten all groups
                tasks = taskData.groups.flatMap(group => group.tasks || []);
            } else {
                return [];
            }

            return tasks
                .map(task => task.workstream_repr)
                .filter(workstream => workstream && workstream.trim() !== '')
                .filter((workstream, index, arr) => arr.indexOf(workstream) === index); // Remove duplicates
        };

        const taskWorkstreamNames = getAllWorkstreamsFromTaskData();
        taskWorkstreamNames.forEach(workstreamName => {
            if (!workstreamMap.has(workstreamName)) {
                workstreamMap.set(workstreamName, {
                    id: `task_${workstreamName}`,
                    name: workstreamName,
                    source: 'task',
                });
            }
        });

        return Array.from(workstreamMap.values());
    };

    const allWorkstreams = getAllWorkstreams();

    const handleAddWorkstream = async () => {
        if (!newWorkstreamName.trim()) return;

        setIsAdding(true);
        try {
            await dispatch(PostProjectTaskWorkstream({ name: newWorkstreamName.trim() }));
            setNewWorkstreamName('');
        } catch (error) {
            console.error('Error adding workstream:', error);
        } finally {
            setIsAdding(false);
        }
    };

    const handleKeyPress = e => {
        if (e.key === 'Enter') {
            handleAddWorkstream();
        }
    };

    const handleWorkstreamClick = workstream => {
        onWorkstreamSelect(workstream);
        setOpen(false);
    };

    return (
        <Menu open={open} onOpenChange={(e, data) => setOpen(data.open)}>
            <MenuTrigger>{children}</MenuTrigger>
            <MenuPopover className={styles.entitySelectorPopover}>
                <MenuList className={styles.entitySelectorList}>
                    <div className={styles.entitySelectorItems}>
                        {allWorkstreams.length > 0 ? (
                            allWorkstreams.map(workstream => {
                                const isSelected = !!selectedWorkstreamName && workstream.name === selectedWorkstreamName;
                                return (
                                    <div
                                        key={workstream.id}
                                        ref={isSelected ? selectedItemRef : undefined}
                                        onClick={() => handleWorkstreamClick(workstream)}
                                        className={`${styles.entitySelectorItem} ${isSelected ? styles.entitySelectorItemSelected : ''}`}>
                                        <span className={styles.entitySelectorItemLabel}>{workstream.name}</span>
                                    </div>
                                );
                            })
                        ) : (
                            <div className={styles.entitySelectorEmpty}>No workstreams available</div>
                        )}
                    </div>

                    {selectedWorkstreamName ? (
                        <>
                            <div className={styles.entitySelectorDivider} />
                            <div
                                className={styles.entitySelectorRemove}
                                onClick={() => {
                                    onWorkstreamSelect(null);
                                    setOpen(false);
                                }}>
                                <span className={styles.entitySelectorRemoveLabel}>Remove Workstream</span>
                                <Delete16Regular className={styles.entitySelectorRemoveIcon} />
                            </div>
                        </>
                    ) : null}

                    <div className={styles.entitySelectorDivider} />
                    <div className={styles.entitySelectorAddRow}>
                        <input
                            type="text"
                            placeholder="Add new workstream"
                            className={styles.entitySelectorInput}
                            value={newWorkstreamName}
                            onChange={e => setNewWorkstreamName(e.target.value)}
                            onKeyPress={handleKeyPress}
                        />
                        <button
                            type="button"
                            className={styles.entitySelectorAddBtn}
                            onClick={handleAddWorkstream}
                            disabled={!newWorkstreamName.trim() || isAdding}
                            aria-label="Add workstream">
                            <Add16Regular />
                        </button>
                    </div>
                </MenuList>
            </MenuPopover>
        </Menu>
    );
};

export default WorkstreamManagementMenu;
